/*
 * Live "Sign in with Shop" login: refresh the account drawer in place, with no
 * page reload, and show a loader while we wait for the customer session.
 *
 * Sign in with Shop authenticates in a popup and establishes the storefront
 * `customer` session without navigating the page (confirmed: the session
 * becomes available a few seconds after the user completes the passkey/SMS
 * step, and the page never reloads on its own).
 *
 * Flow:
 *   1. The user clicks the <shop-login-button> — a popup opens (the window
 *      blurs). We start polling the account content via the Section Rendering
 *      API, cache-busted so we never read a stale logged-out response.
 *   2. When the popup closes and focus returns to the page, we show a loader in
 *      the drawer(s) and poll immediately — by this point the session is
 *      usually already set, so confirmation takes a poll or two.
 *   3. Once the fetched markup is the logged-in variant, we swap it into every
 *      [data-account-content] wrapper (desktop flyout + mobile drawer), which
 *      removes the loader, and dispatch 'account-content:updated' so the
 *      recently-viewed slider re-renders.
 *
 * We only swap when the markup is actually logged-in, so a mis-fire can never
 * blank the drawer; the loader has its own short safety timeout so it can't get
 * stuck if the user abandons the login.
 */
(function () {
  var SECTION = 'account-content';
  var POLL_MS = 2000;
  var TIMEOUT_MS = 120000;   // stop polling after this long
  var LOADER_MAX_MS = 8000;  // never show the loader longer than this

  var watching = false;
  var popupOpened = false;    // window blurred since we armed → a popup is open
  var loaderActive = false;
  var cacheBust = 0;
  var pollNow = null;         // active session's immediate-poll fn, else null

  function sectionNode(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    // Section Rendering wraps output in #shopify-section-<id>; its children are
    // the account-actions markup.
    return doc.querySelector('[id^="shopify-section-"]') || doc.body;
  }

  function isLoggedIn(node) {
    // .side-account__details only renders in the logged-in branch;
    // shop-login-button only renders in the logged-out branch.
    var hasDetails = !!node.querySelector('.side-account__details');
    var hasLoginButton = !!node.querySelector('shop-login-button');
    return hasDetails || !hasLoginButton;
  }

  function fetchSection() {
    var url = '/?sections=' + SECTION + '&__cb=' + (++cacheBust);
    return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (json) { return sectionNode(json[SECTION] || ''); });
  }

  function showLoader() {
    if (loaderActive) return;
    loaderActive = true;
    document.querySelectorAll('[data-account-content]').forEach(function (w) {
      if (w.querySelector('.account-content__loader')) return;
      var overlay = document.createElement('div');
      overlay.className = 'account-content__loader';
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'polite');
      overlay.setAttribute('aria-label', 'Signing you in');
      overlay.innerHTML = '<div class="account-content__spinner"></div>';
      w.appendChild(overlay);
    });
    // Safety: don't let the loader linger if login is abandoned or stalls.
    setTimeout(function () { if (loaderActive) hideLoader(); }, LOADER_MAX_MS);
  }

  function hideLoader() {
    loaderActive = false;
    document.querySelectorAll('.account-content__loader').forEach(function (el) {
      el.remove();
    });
  }

  function applyContent(node) {
    var html = node.innerHTML;
    document.querySelectorAll('[data-account-content]').forEach(function (dest) {
      dest.innerHTML = html; // replaces content, removing any loader overlay
    });
    loaderActive = false;
    document.dispatchEvent(new CustomEvent('account-content:updated'));
  }

  function startWatching() {
    if (watching) return;
    watching = true;
    popupOpened = false;
    var t0 = performance.now();
    var done = false;

    function stop() {
      done = true;
      clearInterval(timer);
      watching = false;
      pollNow = null;
    }

    function poll() {
      return fetchSection().then(function (node) {
        if (done) return; // guard concurrent in-flight polls
        if (isLoggedIn(node)) {
          stop();
          applyContent(node);
        } else if (performance.now() - t0 > TIMEOUT_MS) {
          stop();
          hideLoader();
        }
      }).catch(function () { /* transient; next tick retries */ });
    }

    var timer = setInterval(poll, POLL_MS);
    pollNow = poll; // let the focus handler trigger an immediate check
  }

  // Arm when the user clicks the Shop login button. Clicks are composed, so the
  // host element appears in the event path even though it lives in shadow DOM.
  document.addEventListener('click', function (e) {
    var path = e.composedPath ? e.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      if (n && n.tagName && n.tagName.toLowerCase() === 'shop-login-button') {
        startWatching();
        break;
      }
    }
  }, true);

  // Track popup open (blur) / close (focus). Only treat a focus as "popup
  // closed" if the window blurred first — that confirms a popup actually opened
  // (graceful no-op for any flavor of the flow that never blurs the window).
  window.addEventListener('blur', function () {
    if (watching) popupOpened = true;
  });
  // When the popup closes and focus returns, show the loader and check right
  // away rather than waiting for the next interval tick.
  function onReturn() {
    if (!watching || !popupOpened || !pollNow) return;
    showLoader();
    pollNow();
  }
  window.addEventListener('focus', onReturn);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') onReturn();
  });
})();
